import React, { useState, useEffect, useRef } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { IoClose, IoCheckmark, IoCheckmarkCircleOutline } from "react-icons/io5";
import { HiOutlineInformationCircle, HiOutlinePencil } from "react-icons/hi";
import { PiPaperPlaneTiltBold } from "react-icons/pi";
import { toast } from "react-hot-toast";
import { capture } from "../instrument";
import API from "../api";
import { EMAIL_TYPE_COLORS, EMAIL_TYPE_LABELS, EMAIL_TYPE } from "../utils";

const EmailPreviewModal = ({ admissionSelected, onClose, setLoading, loading, type = EMAIL_TYPE.MORE_QUESTIONS }) => {
  const [selectedTemplate, setSelectedTemplate] = useState(type);
  const [selectedGuidelines, setSelectedGuidelines] = useState(new Set());
  const [customMessage, setCustomMessage] = useState("");
  const [previewHtml, setPreviewHtml] = useState("");
  const [senderEmail, setSenderEmail] = useState("noreply@querco.co");
  const [previewLoading, setPreviewLoading] = useState(false);
  const abortControllerRef = useRef(null);

  const unansweredQuestionRecords = admissionSelected?.questionRecords?.filter((qr) => qr.canAnswerInPreRegistration && qr.patient && !qr.answer && !qr.autoAnswer) || [];
  const guidelines = admissionSelected?.guidelines?.filter((g) => g.canShowInPreRegistration && g.patient) || [];
  const patientEmail = admissionSelected?.patient?.email || "Non renseigné";
  const patientName = `${admissionSelected?.patient?.firstName || ""} ${admissionSelected?.patient?.lastName || ""}`.trim();
  const labName = admissionSelected?.laboratory?.name || "Querco";
  const admissionNumber = `${admissionSelected?.number || ""}${(admissionSelected?.silReservationNumber || admissionSelected?.silAdmissionNumber) ? ` (réf. ${admissionSelected?.silReservationNumber || admissionSelected?.silAdmissionNumber})` : ""}`;
  const EMAIL_TEMPLATES = [
    {
      id: EMAIL_TYPE.CONFIRMATION,
      label: EMAIL_TYPE_LABELS[EMAIL_TYPE.CONFIRMATION],
      description: "Envoyer l'email de confirmation avec le bouton de présence",
      icon: IoCheckmarkCircleOutline,
      subject: `Confirmation de votre demande d'admission n°${admissionNumber}`,
    },
    {
      id: EMAIL_TYPE.MORE_QUESTIONS,
      label: EMAIL_TYPE_LABELS[EMAIL_TYPE.MORE_QUESTIONS],
      description: "Demander des informations supplementaires au patient",
      icon: HiOutlineInformationCircle,
      subject: `Informations complementaires - Admission n°${admissionNumber}`,
    },
    {
      id: EMAIL_TYPE.CUSTOM,
      label: EMAIL_TYPE_LABELS[EMAIL_TYPE.CUSTOM],
      description: "Envoyer un message libre au patient",
      icon: HiOutlinePencil,
      subject: `Message concernant votre admission n°${admissionNumber}`,
    },
    {
      id: EMAIL_TYPE.REJECTION,
      label: EMAIL_TYPE_LABELS[EMAIL_TYPE.REJECTION],
      description: "Informer le patient du refus de sa demande",
      icon: IoClose,
      subject: `Votre demande d'admission n°${admissionNumber} a ete refusée`,
    },
  ];

  useEffect(() => {
    if (selectedTemplate === EMAIL_TYPE.MORE_QUESTIONS || selectedTemplate === EMAIL_TYPE.CONFIRMATION) {
      const guidelineIds = guidelines.map((g) => g.guidelineId);
      setSelectedGuidelines(new Set(guidelineIds));
    } else {
      setSelectedGuidelines(new Set());
    }
  }, [selectedTemplate, admissionSelected]);

  useEffect(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const fetchPreview = async () => {
      setPreviewLoading(true);
      abortControllerRef.current = new AbortController();

      try {
        const { ok, data } = await API.post(
          "/email",
          {
            admissionId: admissionSelected._id,
            type: selectedTemplate,
            customMessage: customMessage.trim(),
            selectedGuidelineIds: Array.from(selectedGuidelines),
            dryRun: true,
          },
          { signal: abortControllerRef.current.signal },
        );
        if (ok && data?.html) {
          setPreviewHtml(data.html);
          if (data.from) setSenderEmail(data.from);
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          capture(error);
          toast.error("Une erreur est survenue lors de la génération de l'aperçu de l'email");
        }
      }
      setPreviewLoading(false);
    };

    const timeout = setTimeout(fetchPreview, 300);
    return () => {
      clearTimeout(timeout);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [selectedTemplate, customMessage, selectedGuidelines, admissionSelected._id]);

  const sendEmail = async () => {
    if (selectedTemplate === EMAIL_TYPE.CUSTOM && !customMessage.trim()) {
      toast.error("Veuillez saisir un message personnalise");
      return;
    }

    setLoading(true);
    try {
      const { ok } = await API.post("/email", {
        admissionId: admissionSelected._id,
        type: selectedTemplate,
        customMessage: customMessage.trim(),
        selectedGuidelineIds: Array.from(selectedGuidelines),
      });

      if (!ok) throw new Error("Failed to send email");
      toast.success("Email envoye avec succes");

      onClose();
    } catch (error) {
      capture(error);
      toast.error("Une erreur est survenue lors de l'envoi de l'email");
    }
    setLoading(false);
  };

  useHotkeys("mod+enter", sendEmail, { enabled: !loading, enableOnFormTags: true, preventDefault: true }, [sendEmail, loading]);

  const currentTemplate = EMAIL_TEMPLATES.find((t) => t.id === selectedTemplate) || EMAIL_TEMPLATES[0];
  const showGuidelines = (selectedTemplate === EMAIL_TYPE.MORE_QUESTIONS || selectedTemplate === EMAIL_TYPE.CONFIRMATION) && guidelines.length > 0;
  const showQuestions = selectedTemplate === EMAIL_TYPE.MORE_QUESTIONS && unansweredQuestionRecords.length > 0;
  const allGuidelinesSelected = guidelines.length > 0 && guidelines.every((g) => selectedGuidelines.has(g.guidelineId));

  const getCustomMessageLabel = (template) => {
    if (template === EMAIL_TYPE.REJECTION) return "Motif du refus";
    if (template === EMAIL_TYPE.CUSTOM) return "Votre message";
    return "Message personnalise";
  };

  const getCustomMessagePlaceholder = (template) => {
    if (template === EMAIL_TYPE.REJECTION) return "Expliquez la raison du refus...";
    if (template === EMAIL_TYPE.CUSTOM) return "Saisissez votre message...";
    return "Ajoutez un message personnalise...";
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[51] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-[95vw] max-w-[1600px] h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-white">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
              <PiPaperPlaneTiltBold className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Envoyer un email au patient</h2>
              <p className="text-sm text-gray-500">Sélectionnez un modèle et personnalisez le contenu</p>
            </div>
          </div>
          <button disabled={loading} onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            <IoClose className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 grid grid-cols-12 min-h-0 overflow-hidden">
          {/* Left Panel - Configuration */}
          <div className="col-span-5 overflow-y-auto border-r border-gray-200 bg-gray-50/50">
            <div className="p-5 space-y-5">
              {/* Recipient */}
              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Destinataire</div>
                <div className="bg-white rounded-lg border border-gray-200 p-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-medium bg-primary-500 flex-shrink-0">
                      {(admissionSelected?.patient?.firstName?.[0] || "").toUpperCase()}
                      {(admissionSelected?.patient?.lastName?.[0] || "").toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 text-sm">{patientName || "Patient"}</div>
                      <div className="text-sm text-gray-500 truncate">{patientEmail}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Template Selection */}
              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Modele d'email</div>
                <div className="space-y-2">
                  {EMAIL_TEMPLATES.map((template) => {
                    const isSelected = selectedTemplate === template.id;
                    const colors = EMAIL_TYPE_COLORS[template.id];
                    const Icon = template.icon;
                    return (
                      <label
                        key={template.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          isSelected ? `${colors.bg} ${colors.border}` : "bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isSelected ? colors.bg : "bg-gray-100"}`}>
                          <Icon className={`w-5 h-5 ${isSelected ? colors.icon : "text-gray-400"}`} />
                        </div>
                        <input type="radio" name="template" value={template.id} checked={isSelected} onChange={() => setSelectedTemplate(template.id)} className="sr-only" />
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm font-medium ${isSelected ? "text-gray-900" : "text-gray-700"}`}>{template.label}</div>
                          <div className="text-xs text-gray-500 truncate">{template.description}</div>
                        </div>
                        {isSelected && (
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${colors.text} bg-white`}>
                            <IoCheckmark className="w-3.5 h-3.5" />
                          </div>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Questions - only for more-questions template */}
              {showQuestions && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Questions à compléter</div>
                    <span className="text-xs font-medium text-gray-600 bg-gray-200 px-2 py-0.5 rounded-full">{unansweredQuestionRecords.length}</span>
                  </div>
                  <div className="bg-white rounded-lg border border-gray-200 p-3">
                    <div className="space-y-2">
                      {unansweredQuestionRecords.map((questionRecord, index) => (
                        <div key={questionRecord._id} className="flex items-start gap-2 text-sm">
                          <span className="text-gray-400 font-medium flex-shrink-0 w-5">{index + 1}.</span>
                          <span className="text-gray-700">{questionRecord.patientQuestion || questionRecord.question || ""}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-400 mt-3 pt-3 border-t border-gray-100">Le patient repondra via le lien dans l'email</p>
                  </div>
                </div>
              )}

              {/* Guidelines - only for more-questions template */}
              {showGuidelines && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Recommandations a inclure</div>
                    <button
                      onClick={() => {
                        if (allGuidelinesSelected) setSelectedGuidelines(new Set());
                        else setSelectedGuidelines(new Set(guidelines.map((g) => g.guidelineId)));
                      }}
                      className="text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors"
                    >
                      {allGuidelinesSelected ? "Tout retirer" : "Tout inclure"}
                    </button>
                  </div>
                  <div className="space-y-2">
                    {guidelines.map((guideline) => {
                      const isSelected = selectedGuidelines.has(guideline.guidelineId);
                      return (
                        <label
                          key={guideline.guidelineId}
                          className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                            isSelected ? "bg-primary-50 border-primary-200" : "bg-white border-gray-200 hover:border-gray-300"
                          }`}
                        >
                          <div
                            className={`w-5 h-5 rounded border-2 flex-shrink-0 mt-0.5 flex items-center justify-center transition-all ${
                              isSelected ? "bg-primary-600 border-primary-600" : "border-gray-300 bg-white"
                            }`}
                          >
                            {isSelected && <IoCheckmark className="w-3 h-3 text-white" />}
                          </div>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {
                              const newSelected = new Set(selectedGuidelines);
                              if (newSelected.has(guideline.guidelineId)) newSelected.delete(guideline.guidelineId);
                              else newSelected.add(guideline.guidelineId);
                              setSelectedGuidelines(newSelected);
                            }}
                            className="sr-only"
                          />
                          <span className={`text-sm leading-relaxed ${isSelected ? "text-gray-900" : "text-gray-600"}`}>{guideline.patientGuideline}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Custom Message */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{getCustomMessageLabel(selectedTemplate)}</div>
                  {selectedTemplate === EMAIL_TYPE.CUSTOM ? (
                    <span className="text-xs font-medium text-red-600">Requis</span>
                  ) : (
                    <span className="text-xs text-gray-400">Optionnel</span>
                  )}
                </div>
                <textarea
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  placeholder={getCustomMessagePlaceholder(selectedTemplate)}
                  className="w-full bg-white border border-gray-200 rounded-lg p-3 text-sm text-gray-700 placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all"
                  rows={selectedTemplate === EMAIL_TYPE.CUSTOM || selectedTemplate === EMAIL_TYPE.REJECTION ? 6 : 3}
                />
              </div>
            </div>
          </div>

          {/* Right Panel - Email Preview */}
          <div className="col-span-7 flex flex-col min-h-0 bg-gray-100">
            {/* Email Preview Header */}
            <div className="bg-gray-50 border-b border-gray-200 px-5 py-4 text-sm">
              <div className="space-y-1.5">
                <div className="flex">
                  <span className="text-gray-400 w-14 flex-shrink-0">De</span>
                  <span className="text-gray-700">{labName} &lt;{senderEmail}&gt;</span>
                </div>
                <div className="flex">
                  <span className="text-gray-400 w-14 flex-shrink-0">A</span>
                  <span className="text-gray-700">{patientEmail}</span>
                </div>
                <div className="flex">
                  <span className="text-gray-400 w-14 flex-shrink-0">Objet</span>
                  <span className="text-gray-900 font-medium">{currentTemplate.subject}</span>
                </div>
              </div>
            </div>

            {/* Email Preview - Scrollable */}
            <div className="flex-1 overflow-hidden">
              {previewLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="flex flex-col items-center gap-3">
                    <svg className="animate-spin w-8 h-8 text-primary-600" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span className="text-sm text-gray-500">Chargement de l'aperçu...</span>
                  </div>
                </div>
              ) : (
                <iframe srcDoc={previewHtml} sandbox="allow-same-origin" className="w-full h-full border-0 bg-white" title="Aperçu de l'email" />
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-white flex justify-end items-center">
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors focus:ring-2 focus:ring-offset-1 focus:ring-gray-300 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Annuler
            </button>

            <button
              onClick={sendEmail}
              disabled={loading}
              className="px-5 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition-colors flex items-center gap-2 focus:ring-2 focus:ring-offset-1 focus:ring-primary-500 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {loading ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Envoi en cours...
                </>
              ) : (
                <>
                  <PiPaperPlaneTiltBold className="w-4 h-4" />
                  Envoyer l'email
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailPreviewModal;
